/**
 * 어드민 API 클라이언트 — 2-tier 모델 (Service × MCP Server × API Key).
 *
 * 모든 API 키는 MCP 서버 하위로 귀속된다. `/admin/services/:slug/api-keys` 레거시 엔드포인트는 제거됨.
 * 실제 엔드포인트 정의는 `src/kernel/routes/admin.ts` 참조.
 */

/** 슬라이스 안의 한 kind. UI 시각화용 메타(displayName/color/icon) 포함. */
export type KindDef = {
	readonly name: string
	readonly displayName: string
	readonly description?: string
	readonly color?: string
	readonly icon?: string
	readonly category?: string
}

export type DomainDef = {
	readonly name: string
	readonly displayName: string
	readonly description?: string
	readonly color?: string
}

export type RelationDef = {
	readonly name: string
	readonly displayName: string
	readonly description?: string
	readonly color?: string
	readonly directionality?: "directed" | "undirected"
	readonly inverseOf?: string
	/** alias relation 이름 목록 (UPPER_SNAKE_CASE). 이 이름들이 입력되면 canonical name 으로 자동 정규화된다. */
	readonly aliases?: ReadonlyArray<string>
}

/** (sourceKind)-[relation]->(targetKind) 트리플. shapes 가 비면 모든 트리플 허용. */
export type ShapeDef = {
	readonly sourceKind: string
	readonly relation: string
	readonly targetKind: string
	readonly description?: string
}

/**
 * 온톨로지 슬라이스 — kind/domain/relation 메타 + shape 트리플.
 * 어댑터 read 경계에서 upgradeSlice 로 정규화 (jsonb 직접 저장 방어).
 *
 * **규격화된 그래프 RAG**: 슬라이스 밖 kind / domain / relation / shape 를 가진 `knowledge_upsert` 는
 * 항상 400 으로 거부된다. enforcement 완화 옵션은 존재하지 않는다.
 *
 * `packIds` 는 슬라이스를 구성한 팩 조합의 식별자 — `mergePacks()` 결과로 주입되며
 * 대시보드 Hero/PackPicker 가 현재 선택 상태를 복원할 때 참조한다.
 *
 * `excludedBlockIds` / `excludedShapes` 는 MasterComposer UX 의 제외 상태 추적용 — 서버는
 * 이 필드를 실행 시 재해석하지 않으며, 대시보드가 제외를 적용한 `kinds/shapes` 로 저장한다.
 * 재진입 시 MasterComposer 가 이 필드를 읽어 체크 해제 상태를 복원한다.
 */
export type OntologySlice = {
	readonly presetId?: string
	readonly packIds?: ReadonlyArray<string>
	readonly excludedBlockIds?: ReadonlyArray<string>
	readonly excludedShapes?: ReadonlyArray<string>
	readonly kinds: ReadonlyArray<KindDef>
	readonly domains: ReadonlyArray<DomainDef>
	readonly relations: ReadonlyArray<RelationDef>
	readonly shapes: ReadonlyArray<ShapeDef>
}

/**
 * 서버 preset 카탈로그 응답 단위.
 *
 * `packIds` 는 preset 을 구성한 팩 조합 id 들이며, PackPicker 의 "Starter 라디오" 가
 * 체크박스 상태를 복원할 때 비교 기준으로 쓴다.
 */
export type OntologyPreset = {
	readonly id: string
	readonly displayName: string
	readonly description: string
	readonly icon: string
	readonly packIds: ReadonlyArray<string>
	readonly slice: OntologySlice
}

/** 팩 그룹 — 대시보드 PackPicker 가 탭 분류에 사용. 서버 `PACK_GROUPS` 와 동일. */
export type PackGroup = "foundation" | "build" | "strategy" | "gtm" | "craft" | "cross-cutting"

/**
 * 온톨로지 팩 — 부분 온톨로지 조각. 단일 preset 대신 여러 팩을 조합해 최종 슬라이스를 만든다.
 *
 * 서버 `/admin/ontology/packs` 응답 shape 과 1:1. 슬라이스 레벨 메타
 * (`presetId / packIds`) 는 팩 자체에 없으며 merge 결과에서만 의미를 가진다.
 *
 * `blockIds` 는 팩이 조합하는 온톨로지 블럭 id 순서 — 대시보드 Pack 패널이
 * "Built from N blocks" 라벨이나 블럭 상세 확장에 활용한다.
 */
export type OntologyPack = {
	readonly id: string
	readonly displayName: string
	readonly description: string
	readonly icon: string
	readonly group: PackGroup
	readonly blockIds: ReadonlyArray<string>
	readonly slice: {
		readonly kinds: ReadonlyArray<KindDef>
		readonly domains: ReadonlyArray<DomainDef>
		readonly relations: ReadonlyArray<RelationDef>
		readonly shapes: ReadonlyArray<ShapeDef>
	}
}

/** 블럭 카테고리 — 대시보드 BlockPicker 탭 분류용. 서버 `BLOCK_CATEGORIES` 와 동일. */
export type BlockCategory = "actor" | "content" | "event" | "product" | "place" | "structure" | "decision"

/**
 * 온톨로지 블럭 — `1 블럭 = 1 kind` 엄격 1:1 의 **재사용 가능한 최소 조각**.
 *
 * 서버 `/admin/ontology/blocks` 응답 shape 과 1:1. 블럭 이름/구조는 우리 고유 정의이며
 * schema.org 는 참조(inspired by) 일 뿐 의존하지 않는다 — URL 필드는 존재하지 않는다.
 *
 * 팩이 "도메인 묶음" 이라면 블럭은 "개별 엔티티 축".
 */
/**
 * 블럭의 구조적 예시 — 서버 `/admin/ontology/blocks` 응답에 포함되는 optional 메타.
 *
 * 블럭 카드/Preview 모달에서 "이 kind 가 실제로 어떤 인스턴스로 쓰이는지" 보여주는 UX 용 샘플.
 * 런타임 저장소/검증 대상이 아니며 문자열 리터럴로만 표현된다.
 */
export type ExampleNode = {
	readonly kind: string
	readonly name: string
	readonly displayName?: string
	readonly description?: string
}

export type ExampleEdge = {
	readonly from: ExampleNode
	readonly relation: string
	readonly to: ExampleNode
}

export type BlockExample = {
	readonly node: ExampleNode
	readonly edges?: ReadonlyArray<ExampleEdge>
	readonly summary?: string
}

export type OntologyBlock = {
	readonly id: string
	readonly displayName: string
	readonly description: string
	readonly icon: string
	readonly category: BlockCategory
	readonly slice: {
		readonly kinds: ReadonlyArray<KindDef>
		readonly domains: ReadonlyArray<DomainDef>
		readonly relations: ReadonlyArray<RelationDef>
		readonly shapes: ReadonlyArray<ShapeDef>
	}
	readonly example?: BlockExample
}

/** 슬라이스 사용량 — Hero 의 "X used" 배지 + OrphanInbox 차집합 원천. */
export type OntologyUsage = {
	readonly kinds: Record<string, number>
	readonly domains: Record<string, number>
	readonly relations: Record<string, number>
}

export type OntologyViolationGroup = {
	readonly key: string
	readonly count: number
	readonly sample: ReadonlyArray<string>
}

export type OntologyShapeViolationGroup = {
	readonly sourceKind: string
	readonly relation: string
	readonly targetKind: string
	readonly count: number
	readonly sample: ReadonlyArray<{ readonly fromId: string; readonly toId: string }>
}

/** preview 응답 — 후보 슬라이스 적용 시 위반 항목. */
export type OntologyViolations = {
	readonly unknownKinds: ReadonlyArray<OntologyViolationGroup>
	readonly unknownDomains: ReadonlyArray<OntologyViolationGroup>
	readonly unknownRelations: ReadonlyArray<OntologyViolationGroup>
	readonly shapeViolations: ReadonlyArray<OntologyShapeViolationGroup>
}

/** 서비스 요약 — `/admin/services` 목록/조회 응답의 단위. */
export type ServiceSummary = {
	readonly serviceSlug: string
	readonly displayName: string
	readonly description?: string
	readonly ownerEmail: string
	readonly status: "active" | "suspended"
	readonly suspendedReason?: string
	readonly createdAt: string
	readonly ontologySlice: OntologySlice
}

/** MCP 서버 요약 — 서비스 하위에 존재하는 엔드포인트 인스턴스. */
export type McpServerSummary = {
	readonly id: string
	readonly serviceSlug: string
	readonly name: string
	readonly displayName: string
	readonly description: string
	readonly enabledTools: ReadonlyArray<string>
	readonly createdAt: string
}

/** API 키 요약 — MCP 서버에 귀속된 발급 키 (plaintext 는 발급 직후만 노출). */
export type ApiKeySummary = {
	readonly serviceSlug: string
	readonly mcpServerId: string
	readonly keyId: string
	readonly label: string
	readonly scopes: ReadonlyArray<string>
	readonly createdAt: string
	readonly createdBy?: string
	readonly expiresAt?: string
	readonly lastUsedAt?: string
	readonly revokedAt?: string
}

/** 발급 응답 — `plainKey` 는 이 응답에서만 한 번 노출된다. */
export type IssuedApiKey = {
	readonly plainKey: string
	readonly record: ApiKeySummary
}

/**
 * OAuth 2.1 (DCR + PKCE) 로 발급된 연결 요약.
 *
 * `/admin/services/:slug/oauth/connections` 응답 단위 — 서버 라우트 준비 후 사용.
 * `listOAuthConnections` 가 404 폴백으로 빈 배열을 반환하므로 라우트 미구현 시에도 안전하다.
 */
export type OAuthConnectionSummary = {
	readonly clientId: string
	readonly clientName: string
	readonly serviceSlug: string
	readonly mcpServerId: string
	readonly scopes: ReadonlyArray<string>
	readonly createdAt: string
	readonly lastUsedAt?: string
}

/** 감사 로그 엔트리 — 최신순으로 반환. */
export type AuditEntry = {
	readonly id: number
	readonly at: string
	readonly actor: string
	readonly action: string
	readonly serviceSlug?: string
	readonly target?: string
	readonly diff?: Record<string, unknown>
}

/** 툴 카탈로그 — 대시보드 발급 UI 의 체크박스 매트릭스 소스. */
export type ToolCatalog = {
	readonly catalog: Record<
		string,
		{ readonly category: string; readonly requiredScopes: ReadonlyArray<string> }
	>
	readonly rolePresets: Record<string, ReadonlyArray<string>>
	readonly serverToolPresets: Record<string, ReadonlyArray<string>>
}

/** 401 수신 시 호출되는 핸들러 (useAdminAuth 가 세션 무효화에 사용). */
export type UnauthorizedHandler = () => void

/**
 * admin-client 호출 시 서버가 구조화 에러 (`{error: {code, message}}`) 를 반환한 경우 투수되는
 * 예외. 호출자는 `code === "memory_pack_missing"` 등 특정 분기를 사용자 친화 배너로 변환한다.
 */
export class AdminApiError extends Error {
	constructor(
		readonly status: number,
		readonly code: string,
		message: string
	) {
		super(message)
		this.name = "AdminApiError"
	}
}

/** MCP 서버 생성 입력. */
export type CreateMcpServerInput = {
	readonly name: string
	readonly displayName: string
	readonly description?: string
	readonly enabledTools: ReadonlyArray<string>
}

/** API 키 발급 입력 — MCP 서버 단위. */
export type IssueApiKeyInput = {
	readonly slug: string
	readonly serverName: string
	readonly scopes: ReadonlyArray<string>
	readonly label?: string
	readonly expiresAt?: string
}

/**
 * 그래프 뷰용 노드 — 서버 `KnowledgeNode` 를 Cosmograph 렌더 shape 로 정규화한 것.
 *
 * `id` (not nodeId) / `metadata` (JSON 문자열) — 이유는 components/graph/types.ts 주석 참조.
 */
export type GraphNodeView = {
	readonly id: string
	readonly domain: string
	readonly kind: string
	readonly name: string
	readonly content?: string
	readonly metadata?: string
}

/** 그래프 뷰용 엣지 — 서버 `KnowledgeEdge` 를 Cosmograph 렌더 shape 로 정규화. */
export type GraphEdgeView = {
	readonly id: string
	readonly source: string
	readonly target: string
	readonly relation: string
	readonly confidence?: number
}

/** 샘플 그래프 조회 입력. overview 는 limit 만, ego 는 seeds+hops 도 필요. */
export type SampleGraphInput =
	| { readonly mode: "overview"; readonly limit?: number }
	| {
			readonly mode: "ego"
			readonly seeds: ReadonlyArray<string>
			readonly hops: 1 | 2
			readonly limit?: number
	  }

export type SampleGraphResponse = {
	readonly nodes: ReadonlyArray<GraphNodeView>
	readonly edges: ReadonlyArray<GraphEdgeView>
	readonly truncated: boolean
}

//#region V10 Phase C — Edge Inspector + Timeline Playback 타입
/**
 * Edge 단위 episode 메타 — `edge_list_episodes` 응답 1건.
 *
 * - `invalidatedAt` 세팅 → episode 본체 무력화 (모든 link 비활성)
 * - `linkInvalidatedAt` 세팅 → 이 edge 에서만 비활성 (episode 본체는 다른 edge 에서 유효)
 * - `otherLinkedEdges` — 같은 episode 가 연결된 다른 edges (UI "Shared with" 칩)
 */
export type EpisodeListItem = {
	readonly episodeId: string
	readonly content: string
	readonly source: string
	readonly occurredAt: string
	readonly createdAt: string
	readonly invalidatedAt?: string
	readonly invalidatedReason?: string
	readonly linkedAt: string
	readonly linkInvalidatedAt?: string
	readonly linkInvalidatedReason?: string
	readonly otherLinkedEdges: ReadonlyArray<string>
	readonly metadata?: Readonly<Record<string, unknown>>
}

export type ListEpisodesResponse = {
	readonly edgeKey: string
	readonly strength: number
	readonly activeCount: number
	readonly invalidatedCount: number
	readonly episodes: ReadonlyArray<EpisodeListItem>
}

export type SemanticSearchHit = {
	readonly edgeKey: string
	readonly fromId: string
	readonly toId: string
	readonly relation: string
	readonly strength: number
	readonly episodeCount: number
	readonly topEpisode: {
		readonly episodeId: string
		readonly content: string
		readonly source: string
		readonly occurredAt: string
	}
}

/** Edge 스냅샷 + 동적 strength. `graph@asOf` 응답의 edge 단위. */
export type GraphEdgeWithStrength = {
	readonly fromId: string
	readonly toId: string
	readonly relation: string
	readonly confidence?: number
	readonly properties?: Record<string, unknown>
	readonly strength?: number
	readonly activeEpisodeCount?: number
	readonly invalidAt?: string
}

export type GraphAtAsOfResponse = {
	readonly nodes: ReadonlyArray<GraphNodeView>
	readonly edges: ReadonlyArray<
		GraphEdgeWithStrength & { readonly id: string; readonly source: string; readonly target: string }
	>
	readonly truncated: boolean
	readonly asOf?: string
	readonly cachedAt?: string
}

export type EpisodesTimeRange = {
	readonly min?: string
	readonly max?: string
}

export type EpisodesDensityResponse = {
	readonly buckets: ReadonlyArray<{ readonly bucket: string; readonly count: number }>
	readonly from?: string
	readonly to?: string
}

export type GraphPlaybackEvent = {
	readonly type:
		| "edge_added"
		| "edge_invalidated"
		| "edge_strength_changed"
		| "node_added"
		| "node_invalidated"
	readonly at: string
	readonly target: string
	readonly fromId?: string
	readonly toId?: string
	readonly relation?: string
	readonly strengthBefore?: number
	readonly strengthAfter?: number
}

export type GraphPlaybackResponse = {
	readonly events: ReadonlyArray<GraphPlaybackEvent>
	readonly from: string
	readonly to: string
}

/** `edge_attach_episode` 입력 — 1 episode 를 N edges 에 동시 링크. */
export type AttachEpisodeInput = {
	readonly toEdges: ReadonlyArray<{
		readonly fromId: string
		readonly toId: string
		readonly relation: string
	}>
	readonly content: string
	readonly source?: string
	readonly occurredAt?: string
	readonly metadata?: Record<string, unknown>
	readonly idempotencyKey?: string
}

export type AttachEpisodeResult = {
	readonly episodeId: string
	readonly linkedEdgeKeys: ReadonlyArray<string>
}

export type LinkEpisodeInput = {
	readonly episodeId: string
	readonly fromId: string
	readonly toId: string
	readonly relation: string
}

export type SemanticSearchInput = {
	readonly query: string
	readonly topK?: number
	readonly relations?: ReadonlyArray<string>
	readonly asOf?: string
	readonly minSimilarity?: number
}

export type InvalidateEdgeInput = {
	readonly fromId: string
	readonly toId: string
	readonly relation: string
	readonly invalidAt?: string
	readonly reason?: string
}

// ─── Workbench Agent Chat 타입 ───────────────────────────────────────

/** agent-chat 요청 타입. */
export type AgentChatRequest = {
	readonly serviceSlug: string
	readonly messages: ReadonlyArray<{ readonly role: "user" | "assistant"; readonly content: string }>
	readonly confirmMode?: "auto" | "confirm"
}

/** agent-chat 응답 내 개별 도구 호출 기록. */
export type AgentToolCall = {
	readonly name: string
	readonly input: unknown
	readonly output?: unknown
	readonly pending?: boolean
	readonly error?: string
}

/** agent-chat 응답 타입. */
export type AgentChatResponse = {
	readonly text: string
	readonly toolCalls: ReadonlyArray<AgentToolCall>
}

/** Document bulk upload 응답 — admin 라우트 `/admin/services/:slug/documents/bulk` 의 결과. */
export type BulkUploadDocumentsResponse = {
	readonly dryRun: boolean
	readonly concurrency: number
	readonly totalItems: number
	readonly successCount: number
	readonly failureCount: number
	readonly estimatedTotalTokens?: number
	readonly estimatedCostUsd?: number
	readonly estimatedDurationMs?: number
	readonly warningHighCost?: boolean
	readonly warningLongDuration?: boolean
	readonly items: ReadonlyArray<{
		readonly documentId: string
		readonly ok: boolean
		readonly estimatedTokens?: number
		readonly estimatedBodyBytes?: number
		readonly created?: boolean
		readonly version?: {
			readonly documentId: string
			readonly version: string
			readonly bodyHash: string
			readonly createdAt: string
		}
		readonly errorCode?: string
		readonly errorMessage?: string
	}>
}

//#endregion

export class AdminClient {
	constructor(
		private readonly baseUrl: string,
		private readonly onUnauthorized?: UnauthorizedHandler
	) {}

	/**
	 * 툴 카탈로그 + 프리셋 조회.
	 * @returns 카탈로그(툴별 카테고리/필수스코프) + role presets + server tool presets
	 */
	async getToolCatalog(): Promise<ToolCatalog> {
		const res = await this.fetch("/admin/tool-catalog")
		return (await res.json()) as ToolCatalog
	}

	/**
	 * 서비스 목록 조회.
	 */
	async listServices(): Promise<ReadonlyArray<ServiceSummary>> {
		const res = await this.fetch("/admin/services")
		const body = (await res.json()) as { services: ReadonlyArray<ServiceSummary> }
		return body.services
	}

	/**
	 * 서비스 단건 조회.
	 * @param slug 서비스 slug
	 */
	async getService(slug: string): Promise<ServiceSummary> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}`)
		const body = (await res.json()) as { service: ServiceSummary }
		return body.service
	}

	/**
	 * 서비스 등록. 성공 시 `/mcp/:slug` (기본 서버) 엔드포인트가 활성화된다.
	 */
	async registerService(input: {
		readonly serviceSlug: string
		readonly displayName: string
		readonly ownerEmail: string
		readonly description?: string
	}): Promise<ServiceSummary> {
		const res = await this.fetch("/admin/services", {
			method: "POST",
			body: JSON.stringify(input)
		})
		const body = (await res.json()) as { service: ServiceSummary }
		return body.service
	}

	async deleteService(slug: string): Promise<void> {
		await this.fetch(`/admin/services/${encodeURIComponent(slug)}`, { method: "DELETE" })
	}

	/**
	 * 서비스 일시정지. MCP 엔드포인트가 403 을 반환하도록 전환된다.
	 * @param slug 서비스 slug
	 * @param reason 정지 사유 (선택)
	 */
	async suspendService(slug: string, reason?: string): Promise<void> {
		await this.fetch(`/admin/services/${encodeURIComponent(slug)}/suspend`, {
			method: "POST",
			body: JSON.stringify(reason !== undefined ? { reason } : {})
		})
	}

	/**
	 * 서비스 재활성화.
	 * @param slug 서비스 slug
	 */
	async activateService(slug: string): Promise<void> {
		await this.fetch(`/admin/services/${encodeURIComponent(slug)}/activate`, {
			method: "POST",
			body: JSON.stringify({})
		})
	}

	/**
	 * 온톨로지 슬라이스 교체.
	 * @param slug 서비스 slug
	 * @param slice 새 슬라이스 (전체 교체)
	 */
	async updateOntologySlice(slug: string, slice: OntologySlice): Promise<void> {
		await this.fetch(`/admin/services/${encodeURIComponent(slug)}/ontology-slice`, {
			method: "PATCH",
			body: JSON.stringify(slice)
		})
	}

	/** 온톨로지 preset 카탈로그. 서비스 slug 무관. */
	async getOntologyPresets(): Promise<ReadonlyArray<OntologyPreset>> {
		const res = await this.fetch("/admin/ontology/presets")
		const body = (await res.json()) as { presets: ReadonlyArray<OntologyPreset> }
		return body.presets
	}

	/**
	 * 온톨로지 팩 카탈로그. 서비스 slug 무관.
	 * 대시보드 PackPicker 가 다중 선택 + 머지 UX 로 사용하며, 각 팩은 `group` 으로 탭 분류된다.
	 */
	async getOntologyPacks(): Promise<ReadonlyArray<OntologyPack>> {
		const res = await this.fetch("/admin/ontology/packs")
		const body = (await res.json()) as { packs: ReadonlyArray<OntologyPack> }
		return body.packs
	}

	/**
	 * 온톨로지 블럭 카탈로그. 서비스 slug 무관.
	 *
	 * 4층 구조(Block → Pack → Preset → Slice) 의 최하단 단위. 블럭 이름/구조는 우리 고유 정의이며
	 * schema.org 는 참조(inspired by) 일 뿐 의존하지 않는다.
	 */
	async getOntologyBlocks(): Promise<ReadonlyArray<OntologyBlock>> {
		const res = await this.fetch("/admin/ontology/blocks")
		const body = (await res.json()) as { blocks: ReadonlyArray<OntologyBlock> }
		return body.blocks
	}

	/**
	 * 마스터 shape 풀 조회 — 블럭이 선언한 shape + cross-domain 연결(ADDITIONAL_MASTER_SHAPES)
	 * 을 dedupe 결합한 "가능한 모든 연결" 목록.
	 *
	 * MasterComposer UX 의 매트릭스 / 토글 원천. 서비스 slug 무관.
	 */
	async getMasterShapes(): Promise<ReadonlyArray<ShapeDef>> {
		const res = await this.fetch("/admin/ontology/shapes")
		const body = (await res.json()) as { masterShapes: ReadonlyArray<ShapeDef>; count: number }
		return body.masterShapes
	}

	/** 슬라이스 사용량. Hero 배지/OrphanInbox 원천. */
	async getOntologyUsage(slug: string): Promise<OntologyUsage> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/ontology-slice/usage`)
		const body = (await res.json()) as { usage: OntologyUsage }
		return body.usage
	}

	/**
	 * 시드 노드 검색 (admin JWT, API 키 불필요).
	 *
	 * `POST /admin/services/:slug/search` → `knowledge_search` 핸들러 재사용.
	 * 하이브리드 검색 (KG fulltext + 벡터 ANN + RRF). overview 가 너무 커서 로드 실패한 경우의
	 * fallback UX — 사용자가 이름/키워드로 노드를 찾아 ego 탐색 시드로 쓴다.
	 */
	/**
	 * Document bulk upload — `/admin/services/:slug/documents/bulk`.
	 *
	 * `dryRun=true` 면 임베딩/저장 없이 토큰/비용 추정만 반환. UI 가 운영자에게 confirm 후 실제 호출.
	 * 응답은 부분 실패를 격리 — `items[].ok=false` 항목이 있어도 200 OK.
	 */
	async bulkUploadDocuments(
		slug: string,
		input: {
			items: ReadonlyArray<{
				documentId: string
				mimeType: "text/markdown" | "text/x-markdown" | "text/plain"
				bodyBase64: string
				summary?: string
				contentType?: string
			}>
			dryRun?: boolean
			concurrency?: number
		}
	): Promise<BulkUploadDocumentsResponse> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/documents/bulk`, {
			method: "POST",
			body: JSON.stringify({
				items: input.items,
				dryRun: input.dryRun ?? false,
				concurrency: input.concurrency ?? 5
			})
		})
		return (await res.json()) as BulkUploadDocumentsResponse
	}

	async searchSeedNodes(
		slug: string,
		query: string,
		limit = 20
	): Promise<
		ReadonlyArray<{ nodeId: string; domain: string; kind: string; name: string; content?: string }>
	> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/search`, {
			method: "POST",
			body: JSON.stringify({ query, limit })
		})
		const body = (await res.json()) as {
			nodes: ReadonlyArray<{
				nodeId: string
				domain: string
				kind: string
				name: string
				content?: string
			}>
		}
		return body.nodes ?? []
	}

	/**
	 * 슬라이스 변경 미리보기 — 후보 슬라이스에 대한 위반 카운트/표본.
	 * Save 직전 PreviewImpactModal 의 데이터 소스. 실제 슬라이스는 변경하지 않는다.
	 * 위반이 있으면 해당 노드들은 저장 후 `knowledge_upsert` 단계에서 거부된다는 뜻이다.
	 */
	async previewOntologySlice(slug: string, candidate: OntologySlice): Promise<OntologyViolations> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/ontology-slice/preview`, {
			method: "POST",
			body: JSON.stringify(candidate)
		})
		const body = (await res.json()) as { violations: OntologyViolations }
		return body.violations
	}

	/**
	 * 감사 로그 조회 (최신순).
	 * @param slug 서비스 slug
	 * @param limit 최대 건수 (기본 200)
	 */
	async listAudit(slug: string, limit?: number): Promise<ReadonlyArray<AuditEntry>> {
		const query = limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : ""
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/audit${query}`)
		const body = (await res.json()) as { audit: ReadonlyArray<AuditEntry> }
		return body.audit
	}

	/**
	 * MCP 서버 목록.
	 * @param slug 서비스 slug
	 */
	async listMcpServers(slug: string): Promise<ReadonlyArray<McpServerSummary>> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/mcp-servers`)
		const body = (await res.json()) as { mcpServers: ReadonlyArray<McpServerSummary> }
		return body.mcpServers
	}

	/**
	 * MCP 서버 생성.
	 * @param slug 서비스 slug
	 * @param input 생성 입력 (name · displayName · description? · enabledTools[])
	 */
	async createMcpServer(slug: string, input: CreateMcpServerInput): Promise<McpServerSummary> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/mcp-servers`, {
			method: "POST",
			body: JSON.stringify({
				name: input.name,
				displayName: input.displayName,
				...(input.description !== undefined ? { description: input.description } : {}),
				enabledTools: input.enabledTools
			})
		})
		const body = (await res.json()) as { mcpServer: McpServerSummary }
		return body.mcpServer
	}

	/**
	 * MCP 서버 단건 조회.
	 * @param slug 서비스 slug
	 * @param name MCP 서버 이름
	 */
	async getMcpServer(slug: string, name: string): Promise<McpServerSummary> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(name)}`
		)
		const body = (await res.json()) as { mcpServer: McpServerSummary }
		return body.mcpServer
	}

	/**
	 * MCP 서버의 enabled_tools 교체.
	 * @param slug 서비스 slug
	 * @param name MCP 서버 이름
	 * @param enabledTools 새 enabled 툴 리스트 (전체 교체)
	 */
	async updateMcpServerTools(slug: string, name: string, enabledTools: ReadonlyArray<string>): Promise<void> {
		await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(name)}/enabled-tools`,
			{
				method: "PATCH",
				body: JSON.stringify({ enabledTools })
			}
		)
	}

	/**
	 * MCP 서버 삭제 (귀속 API 키 cascade).
	 * @param slug 서비스 slug
	 * @param name MCP 서버 이름
	 */
	async deleteMcpServer(slug: string, name: string): Promise<void> {
		await this.fetch(`/admin/services/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(name)}`, {
			method: "DELETE"
		})
	}

	/**
	 * MCP 서버 단위 API 키 목록.
	 * @param slug 서비스 slug
	 * @param serverName MCP 서버 이름
	 */
	async listApiKeys(slug: string, serverName: string): Promise<ReadonlyArray<ApiKeySummary>> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(serverName)}/api-keys`
		)
		const body = (await res.json()) as { apiKeys: ReadonlyArray<ApiKeySummary> }
		return body.apiKeys
	}

	/**
	 * API 키 발급. `plainKey` 는 응답에서만 노출된다.
	 */
	async issueApiKey(input: IssueApiKeyInput): Promise<IssuedApiKey> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(input.slug)}/mcp-servers/${encodeURIComponent(input.serverName)}/api-keys`,
			{
				method: "POST",
				body: JSON.stringify({
					scopes: input.scopes,
					...(input.label !== undefined ? { label: input.label } : {}),
					...(input.expiresAt !== undefined ? { expiresAt: input.expiresAt } : {})
				})
			}
		)
		const body = (await res.json()) as { apiKey: IssuedApiKey }
		return body.apiKey
	}

	/**
	 * API 키 취소.
	 * @param slug 서비스 slug
	 * @param serverName MCP 서버 이름
	 * @param keyId 취소 대상 키 ID
	 */
	async revokeApiKey(slug: string, serverName: string, keyId: string): Promise<void> {
		await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(serverName)}/api-keys/${encodeURIComponent(keyId)}`,
			{ method: "DELETE" }
		)
	}

	/**
	 * KG 샘플 그래프 조회 — 대시보드 Graph 탭.
	 *
	 * 서버 응답(KnowledgeNode/KnowledgeEdge) 을 Cosmograph 렌더 shape(GraphNodeView/GraphEdgeView) 로 정규화한다.
	 * - nodeId → id
	 * - fromId/toId → source/target
	 * - properties → metadata (JSON 문자열, DuckDB-wasm 가 객체에서 Object.keys() 를 불러서)
	 */
	/**
	 * V10 Phase C — Graph@asOf 스냅샷. edge 마다 동적 strength 포함 (UI N+1 방지).
	 *
	 * asOf 생략 시 현재 시점. 1시간 bucket 단위 5분 TTL in-process 캐시 (서버 측).
	 * Cosmograph 렌더 shape 로 정규화해 반환.
	 */
	async getGraphAtAsOf(slug: string, asOf?: string): Promise<GraphAtAsOfResponse> {
		const params = new URLSearchParams()
		if (asOf) params.set("asOf", asOf)
		const qs = params.toString()
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/graph${qs ? `?${qs}` : ""}`)
		const raw = (await res.json()) as {
			nodes: ReadonlyArray<{
				nodeId: string
				domain: string
				kind: string
				name: string
				content?: string
				properties?: Record<string, unknown>
			}>
			edges: ReadonlyArray<{
				fromId: string
				toId: string
				relation: string
				confidence?: number
				properties?: Record<string, unknown>
				strength?: number
				activeEpisodeCount?: number
				invalidAt?: string
			}>
			truncated: boolean
			asOf?: string
			cachedAt?: string
		}
		return {
			nodes: raw.nodes.map((n) => ({
				id: n.nodeId,
				domain: n.domain,
				kind: n.kind,
				name: n.name,
				content: n.content ?? "",
				metadata: n.properties ? JSON.stringify(n.properties) : ""
			})),
			edges: raw.edges.map((e) => ({
				id: `${e.fromId}|${e.toId}|${e.relation}`,
				source: e.fromId,
				target: e.toId,
				fromId: e.fromId,
				toId: e.toId,
				relation: e.relation,
				...(e.confidence !== undefined ? { confidence: e.confidence } : {}),
				...(e.properties ? { properties: e.properties } : {}),
				...(e.strength !== undefined ? { strength: e.strength } : {}),
				...(e.activeEpisodeCount !== undefined ? { activeEpisodeCount: e.activeEpisodeCount } : {}),
				...(e.invalidAt ? { invalidAt: e.invalidAt } : {})
			})),
			truncated: raw.truncated,
			...(raw.asOf ? { asOf: raw.asOf } : {}),
			...(raw.cachedAt ? { cachedAt: raw.cachedAt } : {})
		}
	}

	/** V10 Phase C — 서비스 전체 episodes 의 occurredAt min/max. Timeline Playback slider 의 경계. */
	async getEpisodesTimeRange(slug: string): Promise<EpisodesTimeRange> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/episodes/time-range`)
		return (await res.json()) as EpisodesTimeRange
	}

	/** V10 Phase C — 시간축 density heatmap. buckets 개수 (기본 60, 최대 360). */
	async getEpisodesDensity(slug: string, buckets?: number): Promise<EpisodesDensityResponse> {
		const params = new URLSearchParams()
		if (buckets !== undefined) params.set("buckets", String(buckets))
		const qs = params.toString()
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/episodes/density${qs ? `?${qs}` : ""}`
		)
		return (await res.json()) as EpisodesDensityResponse
	}

	/** V10 Phase C — 재생 구간 diff events 일괄 반환 (클라이언트 로컬 재생용). */
	async getGraphPlayback(slug: string, from: string, to: string): Promise<GraphPlaybackResponse> {
		const params = new URLSearchParams({ from, to })
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/graph/playback?${params.toString()}`
		)
		return (await res.json()) as GraphPlaybackResponse
	}

	/**
	 * V11 — Edge-embedded episodes admin 엔드포인트 사용.
	 * 기존엔 MCP `edge_*` 툴을 callMcp 로 호출해 API 키가 필요했는데, 관리자 페이지 안에서 API 키 요구는
	 * UX 결함이라는 피드백에 따라 admin JWT (쿠키) 로 대체. 7 메서드 모두 `/admin/services/:slug/...` 경로.
	 */
	async attachEpisode(slug: string, input: AttachEpisodeInput): Promise<AttachEpisodeResult> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/episodes/attach`, {
			method: "POST",
			body: JSON.stringify(input)
		})
		const r = (await res.json()) as { ok: true; episodeId: string; linkedEdgeKeys: string[] }
		return { episodeId: r.episodeId, linkedEdgeKeys: r.linkedEdgeKeys }
	}

	async linkEpisode(
		slug: string,
		input: LinkEpisodeInput
	): Promise<{ edgeKey: string; alreadyLinked: boolean }> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/episodes/link`, {
			method: "POST",
			body: JSON.stringify(input)
		})
		const r = (await res.json()) as { ok: true; edgeKey: string; alreadyLinked: boolean }
		return { edgeKey: r.edgeKey, alreadyLinked: r.alreadyLinked }
	}

	async listEpisodes(
		slug: string,
		input: {
			readonly fromId: string
			readonly toId: string
			readonly relation: string
			readonly asOf?: string
			readonly includeInvalidated?: boolean
			readonly limit?: number
		}
	): Promise<ListEpisodesResponse> {
		const params = new URLSearchParams({
			fromId: input.fromId,
			toId: input.toId,
			relation: input.relation
		})
		if (input.asOf) params.set("asOf", input.asOf)
		if (input.includeInvalidated !== undefined)
			params.set("includeInvalidated", String(input.includeInvalidated))
		if (input.limit !== undefined) params.set("limit", String(input.limit))
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/edges/episodes?${params.toString()}`
		)
		return (await res.json()) as ListEpisodesResponse
	}

	async semanticSearchEpisodes(
		slug: string,
		input: SemanticSearchInput
	): Promise<ReadonlyArray<SemanticSearchHit>> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/edges/semantic-search`, {
			method: "POST",
			body: JSON.stringify(input)
		})
		const r = (await res.json()) as { hits: ReadonlyArray<SemanticSearchHit> }
		return r.hits
	}

	async invalidateEpisode(
		slug: string,
		episodeId: string,
		reason?: string
	): Promise<{ alreadyInvalidated: boolean; affectedLinks: number }> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/episodes/invalidate`, {
			method: "POST",
			body: JSON.stringify({ episodeId, ...(reason ? { reason } : {}) })
		})
		const r = (await res.json()) as { ok: true; alreadyInvalidated: boolean; affectedLinks: number }
		return { alreadyInvalidated: r.alreadyInvalidated, affectedLinks: r.affectedLinks }
	}

	async invalidateLink(
		slug: string,
		input: {
			readonly episodeId: string
			readonly fromId: string
			readonly toId: string
			readonly relation: string
			readonly reason?: string
		}
	): Promise<{ edgeKey: string; alreadyInvalidated: boolean }> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/edges/link-invalidate`, {
			method: "POST",
			body: JSON.stringify(input)
		})
		const r = (await res.json()) as { ok: true; edgeKey: string; alreadyInvalidated: boolean }
		return { edgeKey: r.edgeKey, alreadyInvalidated: r.alreadyInvalidated }
	}

	async invalidateEdge(
		slug: string,
		input: InvalidateEdgeInput
	): Promise<{ edgeKey: string; invalidAt: string; alreadyInvalidated: boolean }> {
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/edges/invalidate`, {
			method: "POST",
			body: JSON.stringify(input)
		})
		const r = (await res.json()) as {
			ok: true
			edgeKey: string
			invalidAt: string
			alreadyInvalidated: boolean
		}
		return { edgeKey: r.edgeKey, invalidAt: r.invalidAt, alreadyInvalidated: r.alreadyInvalidated }
	}

	// ─── OAuth 연결 관리 ─────────────────────────────────────────────

	/**
	 * OAuth 2.1 (DCR + PKCE) 로 발급된 연결(oauth_clients) 목록 조회.
	 *
	 * 서버 라우트(`/admin/services/:slug/oauth/connections`) 미구현 시 404 → 빈 목록으로 우아 폴백.
	 * 실제 라우트가 준비된 후 자동으로 활성화된다.
	 *
	 * @param slug 서비스 slug
	 * @returns OAuth 연결 목록. 라우트 미구현 시 빈 배열.
	 */
	async listOAuthConnections(slug: string): Promise<ReadonlyArray<OAuthConnectionSummary>> {
		const res = await fetch(`${this.baseUrl}/admin/services/${encodeURIComponent(slug)}/oauth/connections`, {
			credentials: "include",
			headers: { "content-type": "application/json" }
		})
		// 라우트 미구현(404) — 빈 목록으로 우아 폴백
		if (res.status === 404) return []
		if (res.status === 401) this.onUnauthorized?.()
		if (!res.ok) {
			const text = await res.text()
			throw new Error(`admin /oauth/connections ${res.status}: ${text}`)
		}
		const body = (await res.json()) as { connections: ReadonlyArray<OAuthConnectionSummary> }
		return body.connections
	}

	/**
	 * OAuth 연결 회수 — 발급된 client_id 를 삭제해 해당 연결을 즉시 비활성화한다.
	 *
	 * @param slug 서비스 slug
	 * @param clientId 회수 대상 OAuth client_id
	 */
	async revokeOAuthConnection(slug: string, clientId: string): Promise<void> {
		await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/oauth/connections/${encodeURIComponent(clientId)}`,
			{ method: "DELETE" }
		)
	}

	// ─────────────────────────────────────────────────────────────────

	async getSampleGraph(slug: string, opts: SampleGraphInput): Promise<SampleGraphResponse> {
		const params = new URLSearchParams()
		params.set("mode", opts.mode)
		if (opts.limit != null) params.set("limit", String(opts.limit))
		if (opts.mode === "ego") {
			params.set("seeds", opts.seeds.join(","))
			params.set("hops", String(opts.hops))
		}
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/kg/graph?${params.toString()}`)
		const raw = (await res.json()) as {
			nodes: ReadonlyArray<{
				nodeId: string
				domain: string
				kind: string
				name: string
				content?: string
				properties?: Record<string, unknown>
			}>
			edges: ReadonlyArray<{
				fromId: string
				toId: string
				relation: string
				confidence?: number
			}>
			truncated: boolean
		}
		return {
			nodes: raw.nodes.map((n) => ({
				id: n.nodeId,
				domain: n.domain,
				kind: n.kind,
				name: n.name,
				content: n.content ?? "",
				metadata: n.properties ? JSON.stringify(n.properties) : ""
			})),
			edges: raw.edges.map((e) => ({
				id: `${e.fromId}|${e.toId}|${e.relation}`,
				source: e.fromId,
				target: e.toId,
				relation: e.relation,
				...(e.confidence != null ? { confidence: e.confidence } : {})
			})),
			truncated: raw.truncated
		}
	}

	// ─── Workbench Agent Chat (다단 tool_use) ────────────────────────────
	/**
	 * 에이전트 채팅 — 4 도구(search_blocks · search_packs · suggest_slice · save_slice_to_service)를
	 * 활용한 다단 tool_use 대화.
	 *
	 * POST /admin/agent-chat. JWT 인증 (쿠키). 서버 stateless — 대화 히스토리는 호출자가
	 * localStorage 에 보관해 매 턴 전체 messages 를 보낸다.
	 *
	 * confirmMode:
	 *  - "confirm" (기본): mutation 도구 호출 전 UI 에서 사용자 승인 대기 (pending 플래그 반환).
	 *  - "auto": mutation 도구를 즉시 실행.
	 */
	async agentChat(input: AgentChatRequest): Promise<AgentChatResponse> {
		const res = await this.fetch("/admin/agent-chat", {
			method: "POST",
			body: JSON.stringify(input)
		})
		return (await res.json()) as AgentChatResponse
	}

	private async fetch(path: string, init?: RequestInit): Promise<Response> {
		const res = await fetch(this.baseUrl + path, {
			...init,
			credentials: "include",
			headers: {
				"content-type": "application/json",
				...(init?.headers ?? {})
			}
		})
		if (res.status === 401) this.onUnauthorized?.()
		if (!res.ok) {
			const text = await res.text()
			// 서버 공통 에러 shape `{error: {code, message?}}` 를 우선 파싱 — 호출자가 code 분기 가능.
			try {
				const body = JSON.parse(text) as { error?: { code?: string; message?: string } }
				if (body.error?.code) {
					throw new AdminApiError(res.status, body.error.code, body.error.message ?? text)
				}
			} catch (err) {
				if (err instanceof AdminApiError) throw err
				// JSON 파싱 실패 — 일반 에러로 fallthrough
			}
			throw new Error(`admin ${path} ${res.status}: ${text}`)
		}
		return res
	}

	// ─── Phase 4 Associations ────────────────────────────────────────────

	async getAssociationMetricsTimeline(
		slug: string,
		days?: number
	): Promise<ReadonlyArray<AssociationMetricsRecord>> {
		const qs = days !== undefined ? `?days=${days}` : ""
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/associations/metrics${qs}`)
		const body = (await res.json()) as { items: ReadonlyArray<AssociationMetricsRecord> }
		return body.items
	}

	async recordAssociationMetrics(slug: string, recordedDate?: string): Promise<AssociationMetricsRecord> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/associations/metrics/record/run`,
			{ method: "POST", body: JSON.stringify(recordedDate ? { recordedDate } : {}) }
		)
		return (await res.json()) as AssociationMetricsRecord
	}

	async listAssociationStaging(
		slug: string,
		options?: {
			status?: "pending" | "promoted" | "rejected"
			hintSource?: "co-occurrence" | "embedding-similarity" | "llm-inferred" | "manual"
			limit?: number
		}
	): Promise<ReadonlyArray<AssociationStagingItem>> {
		const params = new URLSearchParams()
		if (options?.status) params.set("status", options.status)
		if (options?.hintSource) params.set("hint_source", options.hintSource)
		if (options?.limit !== undefined) params.set("limit", String(options.limit))
		const qs = params.toString() ? `?${params}` : ""
		const res = await this.fetch(`/admin/services/${encodeURIComponent(slug)}/associations/staging${qs}`)
		const body = (await res.json()) as { items: ReadonlyArray<AssociationStagingItem> }
		return body.items
	}

	async runCoOccurrencePmi(
		slug: string,
		options: { minCoOccurrence?: number; minPmi?: number; topN?: number } = {}
	): Promise<{ candidateCount: number; submittedCount: number }> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/associations/batch/co-occurrence/run`,
			{ method: "POST", body: JSON.stringify(options) }
		)
		return (await res.json()) as { candidateCount: number; submittedCount: number }
	}

	async runEmbeddingSimilarity(
		slug: string,
		options: { minSimilarity?: number; perNodeTopK?: number; totalLimit?: number } = {}
	): Promise<{ candidateCount: number; submittedCount: number }> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/associations/batch/embedding-similarity/run`,
			{ method: "POST", body: JSON.stringify(options) }
		)
		return (await res.json()) as { candidateCount: number; submittedCount: number }
	}

	async runLlmInfer(
		slug: string,
		text: string,
		existingNodeIds?: ReadonlyArray<string>
	): Promise<{ extractedRelations: number; submittedCount: number }> {
		const res = await this.fetch(
			`/admin/services/${encodeURIComponent(slug)}/associations/batch/llm-infer/run`,
			{
				method: "POST",
				body: JSON.stringify({
					text,
					...(existingNodeIds ? { existingNodeIds: [...existingNodeIds] } : {})
				})
			}
		)
		return (await res.json()) as { extractedRelations: number; submittedCount: number }
	}
}

export type AssociationMetricsRecord = {
	serviceSlug: string
	recordedDate: string
	aliasCount: number
	associationCount: number
	stagingPendingCount: number
	stagingPromotedCount: number
	stagingRejectedCount: number
	computedAt: string
}

export type AssociationStagingItem = {
	id: string
	serviceSlug: string
	fromNodeId: string
	toNodeId: string
	hintSource: "co-occurrence" | "embedding-similarity" | "llm-inferred" | "manual"
	candidateWeight: number
	evidenceCount: number
	submittedAt: string
	decision: "pending" | "promoted" | "rejected"
	decidedAt?: string
	decidedBy?: string
}
